import { JSX, KeyboardEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AccuracyReportsContext } from "../import/AccuracyReportsContext";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles/exports";
import { Waffles } from "@app/shared/waffles/Waffles";
import { checkForEnter } from "@app/shared/utility/checkForEnter";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";

interface AccuracyReportsSelectProjectsProps {
  pageIsValid: (isValid: boolean) => void;
}

interface ProjectsTableData {
  [wafflesKeyColumnName]: string;
  projectName: string;
}

const AccuracyReportsSelectProjects = (props: AccuracyReportsSelectProjectsProps): JSX.Element => {
  const wizardContext = useContext(AccuracyReportsContext);
  const [currentFilter, setCurrentFilter] = useState<string>("");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const hasSelectedProjects = wizardContext.projects?.some((project) => project.isSelected) || false;
    props.pageIsValid(hasSelectedProjects);
  }, [wizardContext.projects, props]);

  const projectsColumnConfig: ColumnConfig = {
    projectName: {
      displayText: "Project",
      style: { verticalAlign: "middle" },
      testId: "accuracy-reports-select-projects-table__projects"
    }
  };

  const filteredProjects = useMemo(() => {
    if (!currentFilter || currentFilter.trim() === "") {
      return wizardContext.projects?.filter(project => project.isVisible !== false) || [];
    }
    
    const filterLower = currentFilter.toLowerCase();
    return wizardContext.projects?.filter(project => 
      project.isVisible !== false && project.name.toLowerCase().includes(filterLower)
    ) || [];
  }, [wizardContext.projects, currentFilter]);

  const projectsTableData: ProjectsTableData[] = useMemo(() => {
    return filteredProjects.map<ProjectsTableData>((project) => {
        return ({
          projectName: project.name,
        [wafflesKeyColumnName]: project.customProjectId.toString()
      });
    });
  }, [filteredProjects]);

  const selectedProjectIds: string[] = useMemo(() => {
    return wizardContext.projects?.filter((project) => project.isSelected).map((p) => p.customProjectId.toString()) || [];
  }, [wizardContext.projects]);

  const handleCheckboxChange = (selectedIds: string[]) => {
    const updatedProjects = wizardContext.projects?.map(project => ({
      ...project,
      isSelected: selectedIds.includes(project.customProjectId.toString())
    })) || [];
    
    wizardContext.setProjects?.(updatedProjects);
    
    const selectedProjects = updatedProjects.filter(p => p.isSelected);
    wizardContext.setSelectedProjects?.(selectedProjects);
  };

  const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    checkForEnter(e, () => updateSearchString(searchRef.current?.value || ""));
  };

  const updateSearchString = (searchValue: string) => {
    setCurrentFilter(searchValue);
    wizardContext.updateProjectVisibility?.(searchValue);
  };

  const customSort = (rows: ProjectsTableData[], key: keyof ProjectsTableData, direction: SortDirections) => {
    const directionMultiplier = direction === SortDirections.Asc ? 1 : -1;
    return rows.sort((a, b) => (a[key] > b[key] ? 1 : -1) * directionMultiplier);
  };

  return (
    <PageSection>
      {wizardContext.loading ? (
        <LoadingSpinner />
      ) : wizardContext.error ? (
        <div className="alert alert-danger">{wizardContext.error}</div>
      ) : (
        <>
          <div className="container mt-3">
            <fieldset className="row">
              <div className="col-12">
                <span className="label-s d-block mb-1">Project Search</span>
                <div className="d-flex">
                  <QESTextInput
                    id="filter-projects"
                    testId="accuracy-reports__search-input"
                    defaultValue=""
                    qesRef={searchRef}
                    label="Project Search"
                    hideLabel={true}
                    onKeyUp={(e) => handleKeyUp(e)}
                    containerClassName="flex-grow-1 me-2"
                    className="bg-white"
                  />
                  <QESButton
                    qesType="secondary"
                    onClick={() => updateSearchString(searchRef.current?.value || "")}
                    qesTestId="accuracy-reports__search-button"
                  >
                    Search
                  </QESButton>
                </div>
              </div>
            </fieldset>
          </div>

          <div className="container">
            <Waffles<ProjectsTableData>
              columns={projectsColumnConfig}
              customSort={customSort}
              data={projectsTableData}
              disableProcessingOverlay={true}
              fullWidth={true}
              handleCheckboxChange={handleCheckboxChange}
              selectedKeys={selectedProjectIds}
              showCheckboxes={true}
              testId="accuracy-reports-projects-table"
            />
          </div>
        </>
      )}
    </PageSection>
  );
};

export default AccuracyReportsSelectProjects;